'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useOptimistic,
  useTransition,
} from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Loader2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useUserLogged } from '@/hooks/useUserLogged';
import { isAdminOrOwner } from '@/lib/rbac';

// Admin-only toggle. Off (default) → admin home; on → member ("my") home.
// State persisted in the URL (?view=member) so refreshes/bookmarks survive,
// and both this component and HomeRouter derive their state from the same
// search param.
export function MyViewToggle() {
  const { user } = useUserLogged();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const isMyView = searchParams?.get('view') === 'member';

  // The switch used to render straight off the search param, so the thumb could
  // not move until `router.replace` had completed a navigation. /home is a
  // dynamic route, so that is a server roundtrip, and the control sat visually
  // dead for its duration -- the one interaction where a delay is felt as
  // sluggishness rather than loading.
  //
  // `useOptimistic` holds the clicked value for exactly as long as the
  // transition is pending, then falls back to `isMyView` on its own. No local
  // state to drift out of sync with the URL, which stays the single source of
  // truth that HomeRouter also reads.
  const [isPending, startTransition] = useTransition();
  const [optimisticIsMyView, setOptimisticIsMyView] = useOptimistic(isMyView);

  // Builds the URL for a given next-view value. Single source of URL
  // construction: the prefetch below calls it with the opposite of the
  // committed value (to warm the way back), and the click handler calls it
  // with the value the click actually produced, so the two can never
  // disagree about how a boolean maps to a URL, but also never disagree
  // about which boolean a given click meant.
  const hrefFor = useCallback(
    (nextIsMyView: boolean) => {
      const params = new URLSearchParams(searchParams?.toString() ?? '');
      if (nextIsMyView) {
        params.set('view', 'member');
      } else {
        params.delete('view');
      }
      const qs = params.toString();
      return qs ? `${pathname}?${qs}` : pathname;
    },
    [pathname, searchParams]
  );

  const targetHref = useMemo(() => hrefFor(!isMyView), [hrefFor, isMyView]);

  // Toggle is admin-only, so we know the user might flip between the two
  // home chunks. Kick off both imports on mount so the inactive one is
  // already cached by the time they click — first toggle is instant
  // instead of waiting for the dynamic chunk to download.
  useEffect(() => {
    void import('./admin/AdminCompletedHomeContent');
    void import('./member/CompletedHomeContent');
  }, []);

  // The JS above is only half of it: flipping the view is a navigation to a
  // different search param, so it also needs that URL's RSC payload. Warming it
  // ahead of the click is what makes the swap feel instant rather than merely
  // acknowledged. Re-runs when `targetHref` changes, so after a flip the way
  // back is warm too.
  //
  // /home is dynamic and has a `loading.tsx`, so per Next's prefetching guide
  // this warms the route down to that boundary rather than the whole payload.
  // Partial, but it is the part that is on the critical path of the click.
  useEffect(() => {
    router.prefetch(targetHref);
  }, [router, targetHref]);

  const handleChange = useCallback(
    (checked: boolean) => {
      // Both inside the transition: `setOptimisticIsMyView` is only allowed in
      // one, and putting the navigation in the same transition is what keeps
      // the optimistic value alive until the new param actually lands.
      startTransition(() => {
        setOptimisticIsMyView(checked);
        router.replace(hrefFor(checked), { scroll: false });
      });
    },
    [hrefFor, router, setOptimisticIsMyView, startTransition]
  );

  if (!isAdminOrOwner(user?.Role)) return null;

  return (
    <label
      htmlFor="home-my-view-toggle"
      className="flex items-center gap-2 cursor-pointer select-none"
    >
      <Switch
        id="home-my-view-toggle"
        checked={optimisticIsMyView}
        onCheckedChange={handleChange}
        aria-label="Toggle my scope"
        aria-busy={isPending}
        className="data-unchecked:bg-ds-border-subtlest [&>span[data-unchecked]]:bg-ds-gray-subtle [&>span[data-checked]]:bg-ds-surface-white"
      />
      <span className="ds-text-body-xs-semibold text-ds-text-subtle">
        My Scope
      </span>
      {/* The knob answers the click immediately, but swapping the two home
          views still costs an RSC payload plus the incoming view's own
          queries. Without a signal the whole page just sits there looking
          broken. The slot is always reserved so nothing shifts when the
          spinner appears. */}
      <span
        className="flex w-3.5 shrink-0 items-center justify-center"
        aria-hidden
      >
        {isPending ? (
          <Loader2
            size={14}
            className="animate-spin text-ds-text-subtle"
            strokeWidth={2.5}
          />
        ) : null}
      </span>
    </label>
  );
}
